'use client';

/**
 * sold-out-service — reads/writes `settings/soldOut` in Firestore.
 *
 * Lets the admin mark/unmark ANY listing (lodge, unit, or any other item ID)
 * as sold out. Firestore is the single source of truth once a doc exists for
 * an item ID; items not present fall back to their static data.
 *
 * Owns the module-level cache used by hooks/useSoldOut.ts (see
 * `getCachedSoldOutMap`/`invalidateSoldOutCache`) — same pattern as
 * hooks/useActivityVisibility.ts, but the cache lives here so both the
 * public hook and the admin write path share one source without a
 * circular import (hooks/useSoldOut.ts imports from this file, never the
 * other way around).
 */

import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '@/lib/firebase';

export type SoldOutMap = Record<string, boolean>;

// Module-level cache — shared across all components on the same page load.
let cachedSoldOut: SoldOutMap | null = null;
let fetchPromise: Promise<SoldOutMap> | null = null;

async function loadSoldOutMap(): Promise<SoldOutMap> {
  try {
    const docRef = doc(db, 'settings', 'soldOut');
    const snapshot = await getDoc(docRef);
    if (snapshot.exists()) {
      return snapshot.data() as SoldOutMap;
    }
    return {};
  } catch (error) {
    console.error('[sold-out-service] Error fetching sold-out map:', error);
    return {};
  }
}

/** Lee settings/soldOut. Devuelve {} si no existe. */
export async function fetchSoldOutMap(): Promise<SoldOutMap> {
  if (cachedSoldOut !== null) {
    return cachedSoldOut;
  }
  if (!fetchPromise) {
    fetchPromise = loadSoldOutMap();
  }
  const data = await fetchPromise;
  cachedSoldOut = data;
  return data;
}

/** Marca/desmarca un item. Merge sobre settings/soldOut. Invalida el cache del hook. */
export async function setItemSoldOut(itemId: string, soldOut: boolean): Promise<void> {
  const docRef = doc(db, 'settings', 'soldOut');
  await setDoc(docRef, { [itemId]: soldOut }, { merge: true });
  invalidateSoldOutCache();
}

/** Invalida el cache en memoria del hook useSoldOut (lo llama setItemSoldOut). */
export function invalidateSoldOutCache(): void {
  cachedSoldOut = null;
  fetchPromise = null;
}

/**
 * Synchronous peek at the cache — used internally by hooks/useSoldOut.ts to
 * hydrate its React state immediately when the data is already cached
 * (same optimization hooks/useActivityVisibility.ts does with its own
 * module-level cache). Not part of the admin-facing contract.
 */
export function getCachedSoldOutMap(): SoldOutMap | null {
  return cachedSoldOut;
}
